import React, { useContext, useState, useEffect } from 'react';
//import { Banner } from '../banner/banner.tsx'
import { ResumenDia } from '../resumendia/ResumenDia.tsx'
import { ResumenTransaccion } from '../resumentransaccion/ResumenTransaccion.tsx'
import authContext from '../../context/auth/authContext.js'
import './dashboard.css';
import { getDashboardUseCase, getSkuDetailUseCase, sellUseCase } from '../../di/usecase_module';
import { handleError } from '../../presentation/utils/handleError';
import { ButtonDashboardGroupSku } from '../btn_sku/ButtonDashboardGroupSku.tsx';
import { useSelector, useDispatch } from 'react-redux';
import { ReporteMovs } from '../reportes/ReporteMovs.tsx';
import { SaleModal } from '@movivendor/mv-components';
import { GetSkuDetailRequestDto } from '../../domain/usecases/sku/GetSkuDetailUseCase';
import { RequestParam } from '../../domain/models/RequestParam';
import logo from '../../assets/img/logo-final.svg';
import { fetchClientDebt, fetchProcessPayment } from '../action/fetchClientDebt';
import { WhatsappMessageSimple } from '../action/whatsappMessage.js';
import { BalanceActions } from '../../presentation/redux/actions/BalanceActions';

const cloneRequestParams = (params = []) => params.map((param) => new RequestParam(
  param.name,
  param.value,
  param.description,
  param.isHidden,
  param.subprod || 0
));

const upsertRequestParam = (params, name, value, description, isHidden, subprod = 0) => {
  const index = params.findIndex((param) => param.name === name);
  if (index !== -1) {
    params[index].value = value;
    return params;
  }

  params.push(new RequestParam(name, value, description, isHidden, subprod));
  return params;
};

const getPaymentOperationId = (debtData, currentOpId, debtResponseData, params) => (
  debtData?.op_id
  || currentOpId
  || debtResponseData?.op_id
  || params.find((param) => param.name === 'op_id')?.value
);

const getDebtIdentifier = (debtData, clientDebtParam, opId, amountParam) => (
  debtData?.selectedDebt?.id
  || clientDebtParam?.value
  || (opId ? amountParam?.value : null)
);

const getDebtAmount = (debtData, debtItem, debtResponseData, amountParam) => (
  debtData?.selectedDebt?.amount
  || debtItem?.amount
  || debtResponseData?.amount
  || amountParam?.value
  || ''
);

const createDebtAttributeParam = (opId, debtId, debtAmount) => (
  new RequestParam(
    'attr',
    [`op_id:${opId}`, `debt_id:${debtId}`, `debt_amount:${debtAmount}`].join('|'),
    'Atributos de deuda',
    true
  )
);

const createEmptyOperationContext = () => ({
  sku: undefined,
  title: '',
  options: undefined,
  currentOpId: null,
  debtResponseData: null
});

function Dashboard() {
  const AuthContext = useContext(authContext);
  const { token, tipo_modelo } = AuthContext;
  const dispatch = useDispatch();
  const [groups, setGroups] = useState([]);
  const resume = useSelector((s) => s.balanceState.balance);

  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(logo);
  const [operationContext, setOperationContext] = useState(createEmptyOperationContext());

  useEffect(() => {
    setLogoUrl(logo);
  }, []);

  const { pendiente_2fa } = resume;

  const handleOpenSaleModal = (sku, title, options) => {
    setOperationContext({
      sku,
      title,
      options,
      currentOpId: null,
      debtResponseData: null
    });
    setIsSaleModalOpen(true);
  };

  const handleCloseSaleModal = () => {
    setOperationContext(createEmptyOperationContext());
    setIsSaleModalOpen(false);
  };

  const handleSkuSelect = async (sku) => {
    try {
      const response = await getSkuDetailUseCase().execute(
        new GetSkuDetailRequestDto(sku.sku)
      );
      return response.elements || [];
    } catch (error) {
      throw error;
    }
  };

  const buildPaymentParams = (params, debtData, context) => {
    const localParams = cloneRequestParams(params);
    const clientDebtParam = localParams.find((param) => param.name === 'clientDebt');
    const amountParam = localParams.find((param) => param.name === 'amount');
    const debtResponseData = context?.debtResponseData;
    const debtItem = debtResponseData?.debts?.items?.[0];
    const opId = getPaymentOperationId(debtData, context?.currentOpId, debtResponseData, localParams);

    if (!amountParam?.value && debtResponseData?.amount) {
      upsertRequestParam(localParams, 'amount', debtResponseData.amount, 'amount', false, 0);
    }

    const hasIdentificationNumber = params.some((param) => param.name === 'identificationNumber')
      || localParams.some((param) => param.name === 'identificationNumber');
    const hasBankCode = params.some((param) => param.name === 'bankCode')
      || localParams.some((param) => param.name === 'bankCode');
    const isRemesa = hasIdentificationNumber && hasBankCode;

    if (isRemesa) {
      return {
        localParams,
        opId,
        shouldUseRemesaFlow: true
      };
    }

    const updatedAmountParam = localParams.find((param) => param.name === 'amount');
    const debtId = getDebtIdentifier(debtData, clientDebtParam, opId, updatedAmountParam);
    const debtAmount = getDebtAmount(debtData, debtItem, debtResponseData, updatedAmountParam);

    if (debtId && opId) {
      const realAmount = debtItem?.amount?.toString() || debtResponseData?.amount?.toString();
      if (realAmount) {
        upsertRequestParam(localParams, 'amount', realAmount, 'amount', false, 0);
      } else {
        console.warn('⚠️ [handleProcessPayment] debtResponseData.amount no disponible, amount no se reemplazará');
      }

      localParams.push(createDebtAttributeParam(opId, debtId, debtAmount));
      upsertRequestParam(localParams, 'ref', debtId, 'Referencia', true);
    } else {
      console.warn('⚠️ [handleProcessPayment] Bloque DEUDA omitido → debt_id:', debtId, '| op_id:', opId);
    }

    return {
      localParams,
      opId,
      shouldUseRemesaFlow: false
    };
  };

  const handleProcessPayment = async (params, debtData) => {
    try {
      const operationSnapshot = operationContext;
      const {
        localParams,
        opId,
        shouldUseRemesaFlow
      } = buildPaymentParams(params, debtData, operationSnapshot);

      if (shouldUseRemesaFlow) {
        if (!opId) {
          console.error('⚠️ ADVERTENCIA: op_id no disponible para remesa');
        }
        const response = await fetchProcessPayment(token, localParams, opId);
        dispatch(BalanceActions.updateBalance(token));
        setOperationContext(createEmptyOperationContext());
        return response?.elements || response || [];
      }

      const response = await sellUseCase().execute(localParams);
      dispatch(BalanceActions.updateBalance(token));

      if (opId) {
        setOperationContext((currentContext) => ({
          ...currentContext,
          currentOpId: opId
        }));
      }

      return response.elements || [];
    } catch (error) {
      console.error('❌ Error en handleProcessPayment:', error);
      throw error;
    }
  }

  useEffect(() => {
    const getData = () => {
      getDashboardUseCase().execute().then((result) => {
        setGroups(result);
      }).catch((e) => {
        handleError(e);
      })
    }
    getData()
  }, [token]);

  const handleFetchClientDebt = async (params) => {
    try {
      const operationSnapshot = operationContext;
      const enrichedParams = [...params];

      if (!enrichedParams.some(p => p.name === 'sku') && operationSnapshot.sku) {
        enrichedParams.push({
          name: 'sku',
          value: operationSnapshot.sku.sku || operationSnapshot.sku,
          description: 'sku',
          isHidden: false
        });
      }

      const result = await fetchClientDebt(token, enrichedParams);
      setOperationContext((currentContext) => ({
        ...currentContext,
        debtResponseData: result
      }));

      const identifier = result?.op_id || result?.confirmation_number || result?.attrs?.op_id || result?.attrs?.OP_ID;
      if (identifier) {
        setOperationContext((currentContext) => ({
          ...currentContext,
          currentOpId: identifier
        }));
      } else {
        console.warn('⚠️ No hay identificador (op_id/confirmation_number/attrs) en el resultado');
      }
      return result;
    } catch (error) {
      console.error('❌ [handleFetchClientDebt] Error:', error);
      throw error;
    }
  };

  return (
    <>
      <div className="row">
        {/* vender mensajes */}
        <div className="col-xl-8 mt-3">
          {pendiente_2fa &&
            <div className="alert alert-warning" role="alert">
              <h4 className="alert-heading">¡Pendiente de Activación 2FA!</h4>
              <small>
                Hemos enviado un correo electrónico a tu dirección con instrucciones para completar el proceso de activación del 2FA. Por favor, revisa tu bandeja de entrada.
              </small>
              <hr />
              <small className="mb-0">Si no recibes el correo en unos minutos, asegúrate de verificar la carpeta de spam y revisa que la dirección de correo proporcionada sea correcta.</small>
            </div>
          }

          {/* <Banner /> */}
          <div className="row">
            <div className="col-md-9 mt-3">
              <h3><small className="text-muted text-left pt-3 pl-5 pb-0 mb-0">Vender</small></h3>
            </div>
          </div>
          {
            groups.map((group) => {
              return <ButtonDashboardGroupSku
                key={group.groupName}
                data={group}
                onOpenSaleModal={handleOpenSaleModal}
              />
            })
          }

        </div>
        <div className=" col-lg-12 col-xl-4 pb-4">
          <div className="container">
            <div className="row">
              <div className="col-md-6 col-lg-6 col-xl-12">
                <ResumenDia />
              </div>
              <div className="col-md-6 col-lg-6 col-xl-12">
                <ResumenTransaccion />
              </div>
              {
                (tipo_modelo) &&
                <div className="col-md-6 col-lg-6 col-xl-12">
                  <ReporteMovs />
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <SaleModal
        isOpen={isSaleModalOpen}
        onClose={handleCloseSaleModal}
        setup={{
          title: operationContext.title,
          sku: operationContext.sku,
          options: operationContext.options,
        }}
        onSkuSelect={handleSkuSelect}
        onProcessPayment={handleProcessPayment}
        onFetchClientDebt={handleFetchClientDebt}
        logoUrl={logoUrl}
        operationId={operationContext.currentOpId}
        onSendReceipt={async (phone, dni, opId) => {
          const response = await WhatsappMessageSimple(token, {
            number: phone,
            document: dni,
            op_id: opId
          });
          return response;
        }}
      />
    </>
  );
}
export default Dashboard;